<template>
  <section
    v-if="
      articleData.title || articleData.subtitle || articleData.blocks.length
    "
    class="article-section"
  >
    <div class="article-content">
      <h2
        v-if="articleData.title"
        class="article-title"
        v-html="formatInline(articleData.title)"
      ></h2>

      <p
        v-if="articleData.subtitle"
        class="article-subtitle"
        v-html="formatInline(articleData.subtitle)"
      ></p>

      <div v-if="articleData.blocks.length" class="article-blocks">
        <template
          v-for="(block, index) in articleData.blocks"
          :key="`article-block-${index}`"
        >
          <h3
            v-if="block.type === 'h1'"
            class="rich-heading rich-heading-large"
            v-html="formatInline(block.text)"
          ></h3>

          <h4
            v-else-if="block.type === 'h2'"
            class="rich-heading rich-heading-medium"
            v-html="formatInline(block.text)"
          ></h4>

          <h5
            v-else-if="block.type === 'h3'"
            class="rich-heading rich-heading-small"
            v-html="formatInline(block.text)"
          ></h5>

          <ul v-else-if="block.type === 'ul'" class="article-list">
            <li
              v-for="(item, itemIndex) in block.items"
              :key="`article-ul-item-${itemIndex}`"
              v-html="formatInline(item)"
            ></li>
          </ul>

          <ol
            v-else-if="block.type === 'ol'"
            class="article-list article-ordered-list"
          >
            <li
              v-for="(item, itemIndex) in block.items"
              :key="`article-ol-item-${itemIndex}`"
              v-html="formatInline(item)"
            ></li>
          </ol>

          <div
            v-else-if="block.type === 'ordered-item'"
            class="article-numbered-item"
          >
            <span class="article-number">{{ block.number }}.</span>
            <div
              class="article-numbered-text"
              v-html="formatInline(block.text)"
            ></div>
          </div>

          <div
            v-else-if="block.type === 'spacer'"
            class="article-spacer"
            aria-hidden="true"
          ></div>

          <p
            v-else-if="block.type === 'label'"
            class="article-label"
            v-html="formatInline(block.text)"
          ></p>

          <p
            v-else
            :class="[
              'article-paragraph',
              {
                'article-paragraph--indented': block.indented,
                'article-paragraph--after-label':
                  articleData.blocks[index - 1]?.type === 'label',
              },
            ]"
            v-html="formatInline(block.text)"
          ></p>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import { computed } from "vue";

export default {
  name: "ArticleSection",

  props: {
    content: {
      type: String,
      default: "",
    },
  },

  setup(props) {
    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    // Google Sheet 支援：
    // **粗體**、*斜體*、{red}紅色{/red}、{big}大字{/big}、{mark}螢光標示{/mark}
    const formatInline = (value) => {
      let html = escapeHtml(value);

      const richTags = {
        red: "rich-red",
        blue: "rich-blue",
        green: "rich-green",
        orange: "rich-orange",
        yellow: "rich-yellow",
        purple: "rich-purple",
        gray: "rich-gray",
        big: "rich-big",
        small: "rich-small",
        mark: "rich-mark",
      };

      Object.entries(richTags).forEach(([tag, className]) => {
        const pattern = new RegExp(
          `\\{${tag}\\}([\\s\\S]+?)\\{\\/${tag}\\}`,
          "gi",
        );

        html = html.replace(pattern, `<span class="${className}">$1</span>`);
      });

      // 超連結：[顯示文字](網址)
      // 例如：[Close Reader](/BookPage/ReadingPage/CloseReader)
      html = html.replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        (match, linkText, url) => {
          const href = String(url || "").trim();

          // 僅允許站內路徑、http(s)、mailto、tel、錨點連結
          if (!/^(?:\/|https?:\/\/|mailto:|tel:|#)/i.test(href)) {
            return match;
          }

          const isExternal = /^https?:\/\//i.test(href);

          return isExternal
            ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${linkText}</a>`
            : `<a href="${href}">${linkText}</a>`;
        },
      );

      html = html.replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>");

      return html;
    };
    const parseBlockLine = (line) => {
      const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);

      if (headingMatch) {
        return {
          type: `h${headingMatch[1].length}`,
          text: headingMatch[2].trim(),
        };
      }

      return {
        type: "paragraph",
        text: line,
      };
    };

    const articleData = computed(() => {
      const raw = String(props.content || "")
        .replace(/\r\n?/g, "\n")
        .trim();

      if (!raw) {
        return {
          title: "",
          subtitle: "",
          blocks: [],
        };
      }

      // 保留每一個原始換行；只在判斷內容時 trim，避免空白行被吃掉。
      const lines = raw.split("\n");
      let title = "";
      let subtitle = "";
      let waitingForSubtitle = false;
      let paragraphLines = [];
      let listItems = [];
      let listType = "";
      const blocks = [];
      let continueIndentedParagraphs = false;

      const pushParagraph = () => {
        if (!paragraphLines.length) return;

        paragraphLines.forEach((entry) => {
          const text = entry.text.trim();
          if (!text) return;

          const isLabel = /^\*\*.+\*\*$/.test(text);

          blocks.push({
            type: isLabel ? "label" : "paragraph",
            text,
            indented: !isLabel && entry.indented,
          });
        });

        paragraphLines = [];
      };

      const pushList = () => {
        if (!listItems.length) return;

        blocks.push({
          type: listType || "ul",
          items: [...listItems],
        });

        listItems = [];
        listType = "";
      };

      lines.forEach((rawLine) => {
        const line = rawLine.trim();

        // 每一個空白行都建立一個 spacer。
        // 換 1 次 = 1 個間隔；換 2 次 = 2 個間隔，以此類推。
        if (!line) {
          pushParagraph();
          pushList();
          blocks.push({ type: "spacer" });
          continueIndentedParagraphs = false;
          return;
        }

        const titleMatch = line.match(/^【\s*(.*?)\s*】$/);

        if (titleMatch) {
          pushParagraph();
          pushList();
          title = titleMatch[1].trim();
          waitingForSubtitle = false;
          continueIndentedParagraphs = false;
          return;
        }

        const subtitleMatch = line.match(
          /^(?:副標|副標題|Subtitle)\s*[：:]\s*(.*)$/i,
        );

        if (subtitleMatch) {
          pushParagraph();
          pushList();

          const subtitleText = subtitleMatch[1].trim();

          if (subtitleText) {
            subtitle = subtitleText;
            waitingForSubtitle = false;
          } else {
            waitingForSubtitle = true;
          }

          continueIndentedParagraphs = false;
          return;
        }

        if (waitingForSubtitle) {
          subtitle = line;
          waitingForSubtitle = false;
          continueIndentedParagraphs = false;
          return;
        }

        const orderedListMatch = line.match(/^(\d+)(?:[.)]\s+|\s+)(.+)$/);

        if (orderedListMatch) {
          pushParagraph();
          pushList();

          blocks.push({
            type: "ordered-item",
            number: orderedListMatch[1],
            text: orderedListMatch[2].trim(),
          });
          continueIndentedParagraphs = true;
          return;
        }

        const unorderedListMatch = line.match(/^[ㆍ・•●▪◦‧-]\s+(.+)$/);

        if (unorderedListMatch) {
          pushParagraph();

          if (listType && listType !== "ul") {
            pushList();
          }

          listType = "ul";
          listItems.push(unorderedListMatch[1].trim());
          continueIndentedParagraphs = true;
          return;
        }

        const block = parseBlockLine(line);

        if (block.type !== "paragraph") {
          pushParagraph();
          pushList();
          blocks.push(block);
          continueIndentedParagraphs = false;
          return;
        }

        pushList();
        paragraphLines.push({
          text: line,
          indented: continueIndentedParagraphs,
        });
      });

      // 把最後尚未遇到空白行的段落／清單補進 blocks
      // 否則 Google Sheet 儲存格的最後一段文字會被漏掉
      pushParagraph();
      pushList();

      return {
        title,
        subtitle,
        blocks,
      };
    });

    return {
      articleData,
      formatInline,
    };
  },
};
</script>

<style scoped>
.article-section {
  width: min(100%, 1040px);
  margin: 0 auto 72px;
  padding: 48px 0;
  border-top: 1px solid #dfe6ec;
  border-bottom: 1px solid #dfe6ec;
}

.article-content {
  width: 100%;
}

.article-title {
  margin: 0 0 12px;
  color: #173f5f;
  font-size: clamp(26px, 3vw, 36px);
  line-height: 1.3;
  text-align: left;
}
.article-subtitle {
  margin: 0 0 28px;
  color: #24618d;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.7;
  text-align: left;
}
.article-blocks {
  color: #4d5d68;
}
.article-paragraph {
  margin: 0 0 2px;
  font-size: 16px;
  line-height: 1.65;
  text-align: left;
}

/* 內縮說明文字：彼此靠近一點，連續換行仍保留 */
.article-paragraph--indented {
  margin-top: 0;
  margin-bottom: 0;
  padding-left: 32px;
}

/* 每一個原始空白行都對應一個固定高度 */
.article-spacer {
  height: 6px;
}

/* 像 **Concrete｜具體操作** 這種完整粗體小標 */
.article-label {
  margin: 10px 0 4px;
  font-size: 16px;
  line-height: 1.65;
  text-align: left;
}

/* 粗體小標下面就是解釋文字，所以縮小兩者距離 */
.article-paragraph--after-label {
  margin-top: 0;
  margin-bottom: 3px;
}

.article-numbered-item {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 4px 0;
  font-size: 16px;
  line-height: 1.65;
  text-align: left;
}

.article-number {
  flex: 0 0 22px;
  text-align: right;
}

.article-numbered-text {
  flex: 1;
  min-width: 0;
}
.rich-heading {
  color: inherit;
  font-weight: 750;
  line-height: 1.4;
  text-align: left;
}
.rich-heading-large {
  margin: 30px 0 14px;
  font-size: 26px;
}
.rich-heading-medium {
  margin: 26px 0 12px;
  font-size: 21px;
}
.rich-heading-small {
  margin: 22px 0 10px;
  font-size: 17px;
}
.article-list {
  margin: 4px 0;
  padding-left: 24px;
  color: #4d5d68;
}

.article-list + .article-paragraph {
  margin-top: 0;
  margin-left: 0;
  padding-left: 32px;
  margin-bottom: 0;
}

.article-list li {
  margin: 0 0 3px;
  font-size: 16px;
  line-height: 1.65;
  text-align: left;
}
.article-list li:last-child {
  margin-bottom: 0;
}

.article-ordered-list {
  list-style-type: decimal;
  padding-left: 28px;
}

.article-ordered-list li {
  padding-left: 4px;
}

:deep(.rich-red) {
  color: #8f1d2c;
}
:deep(.rich-blue) {
  color: #1565c0;
}
:deep(.rich-green) {
  color: #2e7d32;
}
:deep(.rich-orange) {
  color: #d95f02;
}
:deep(.rich-yellow) {
  color: #c58a00;
}
:deep(.rich-purple) {
  color: #6a1b9a;
}
:deep(.rich-gray) {
  color: #757575;
}

:deep(.rich-big) {
  font-size: 1.25em;
  font-weight: 700;
}

:deep(.rich-small) {
  font-size: 0.82em;
}

:deep(.rich-mark) {
  padding: 0.08em 0.25em;
  border-radius: 0.2em;
  background: #fff1a8;
  color: inherit;
}

:deep(strong) {
  color: inherit;
  font-weight: 750;
}

:deep(em) {
  color: inherit;
  font-style: italic;
}

@media (max-width: 600px) {
  .article-section {
    margin-bottom: 48px;
    padding: 36px 0;
  }
  .article-title {
    font-size: 25px;
  }
  .article-subtitle {
    margin-bottom: 22px;
    font-size: 16px;
  }
  .article-paragraph,
  .article-list li,
  .article-numbered-item {
    font-size: 15px;
    line-height: 1.7;
  }

  .article-numbered-item {
    gap: 8px;
    margin: 4px 0;
  }

  .article-paragraph--indented {
    padding-left: 30px;
  }

  .article-label {
    margin-top: 10px;
    margin-bottom: 4px;
    font-size: 15px;
  }
  .rich-heading-large {
    font-size: 22px;
  }
  .rich-heading-medium {
    font-size: 19px;
  }
}
</style>
